'use client';

import { isAttuneDesktopShell } from '@/lib/desktop-auth-client';
import { integrationSetupReturnPath } from '@/lib/integrations/setup-return';
import { useIntegrationSetupFlow } from './useIntegrationSetupFlow';
import { IntegrationSetupError } from './IntegrationSetupError';
import { useConnectorDraft } from './useConnectorDraft';

import { Button, HStack, Input, NativeSelect, Text } from '@chakra-ui/react';
import {
  AlertCircle,
  Check,
  ExternalLink,
  PackageSearch,
  RefreshCw,
  ShoppingBag,
  ShieldCheck,
  Unlink,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/app/auth-provider';
import {
  IntegrationCard,
  IntegrationNotice,
  IntegrationStatusRow,
} from '@/components/shared/integrations/IntegrationCardShell';
import { InlineSpinner } from '@/components/shared/PageLoader';
import { useProject } from '@/contexts/ProjectContext';

interface ShopifyConnection {
  id: string;
  app_config_id?: string | null;
  auth_mode?: 'merchant_client_credentials' | 'partner_authorization_code' | null;
  shop_domain: string;
  status: string;
  mode: 'read_only' | 'write_ready';
  scopes: string[];
  installed_at: string | null;
  updated_at: string | null;
  last_sync_at: string | null;
  capabilities: {
    readProducts: boolean;
    writeProducts: boolean;
    readContent: boolean;
    writeContent: boolean;
    readReports: boolean;
    readOrders: boolean;
    readAbandonedCheckouts: boolean;
    contentCopyUpdates: boolean;
    metafieldsSet: boolean;
    shopifyAnalytics: boolean;
    bulkDescriptionUpdates: boolean;
    rollback: boolean;
  };
  ordersReady?: boolean;
  abandonedCheckoutsReady?: boolean;
  orderAccessStatus?: string;
  abandonedCheckoutAccessStatus?: string;
}

interface ShopifyStatus {
  connected: boolean;
  configured: boolean;
  requiredScopes: string[];
  connections: ShopifyConnection[];
  managedProvisioning?: ShopifyManagedProvisioning | null;
  binding?: ShopifyManagedProvisioning | null;
}

interface ShopifyManagedProvisioning {
  configId?: string;
  config_id?: string;
  shopDomain?: string;
  shop_domain?: string;
  status?: 'draft' | 'active' | 'disabled' | 'error';
  authMode?: 'merchant_client_credentials' | 'partner_authorization_code';
  auth_mode?: 'merchant_client_credentials' | 'partner_authorization_code';
  secretConfigured?: boolean;
  secret_configured?: boolean;
}

function normalizeShopInput(value: string): string {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return '';
  if (trimmed.includes('.')) return trimmed.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  return `${trimmed}.myshopify.com`;
}

export function ShopifyIntegration({ canManage }: { canManage: boolean }) {
  const { user } = useAuth();
  const { currentProject } = useProject();
  const setup = useIntegrationSetupFlow(currentProject?.id);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [protectedAccessChecked, setProtectedAccessChecked] = useState(false);
  const [protectedAccessProbeFailed, setProtectedAccessProbeFailed] = useState(false);
  const protectedProbeRun = useRef(0);
  const [status, setStatus] = useState<ShopifyStatus>({
    connected: false,
    configured: false,
    requiredScopes: [],
    connections: [],
  });
  const [shopDomain, setShopDomain] = useState('');
  const [selectedShopDomain, setSelectedShopDomain] = useState('');

  const activeConnections = useMemo(
    () =>
      status.connections.filter(
        connection => connection.status === 'active' || connection.status === 'warning'
      ),
    [status.connections]
  );
  const activeConnection = useMemo(
    () =>
      activeConnections.find(connection => connection.shop_domain === selectedShopDomain) ||
      activeConnections[0] ||
      null,
    [activeConnections, selectedShopDomain]
  );
  useEffect(() => {
    if (!activeConnections.some(connection => connection.shop_domain === selectedShopDomain)) {
      setSelectedShopDomain(activeConnections[0]?.shop_domain || '');
    }
  }, [activeConnections, selectedShopDomain]);
  const managedProvisioning = status.managedProvisioning || status.binding || null;
  const managedShopDomain =
    managedProvisioning?.shopDomain || managedProvisioning?.shop_domain || '';
  const hasIncompleteManagedProvisioning = Boolean(managedProvisioning && !status.connected);

  const clearConnectionDraft = useConnectorDraft(
    user?.uid,
    currentProject?.id,
    'shopify',
    { shopDomain },
    draft => {
      setShopDomain(draft.shopDomain || '');
    }
  );

  const fetchStatus = useCallback(async () => {
    if (!user || !currentProject?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };
      const response = await fetch(
        `/api/integrations/shopify/status?projectId=${encodeURIComponent(currentProject.id)}`,
        { headers }
      );
      if (!response.ok) throw new Error('Failed to load Shopify status');
      const payload = (await response.json()) as ShopifyStatus;
      const probeRun = protectedProbeRun.current + 1;
      protectedProbeRun.current = probeRun;
      setProtectedAccessChecked(false);
      setProtectedAccessProbeFailed(false);
      setStatus(payload);
      const firstShop = payload.connections[0]?.shop_domain;
      if (firstShop) setShopDomain(current => current || firstShop);

      void (async () => {
        let probeSucceeded = false;
        try {
          const permissionsResponse = await fetch('/api/attune/shopify/permissions-status', {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ projectId: currentProject.id }),
          });
          if (!permissionsResponse.ok || protectedProbeRun.current !== probeRun) {
            if (protectedProbeRun.current === probeRun) setProtectedAccessProbeFailed(true);
            return;
          }
          const permissions = (await permissionsResponse.json()) as {
            connections?: ShopifyConnection[];
          };
          const permissionById = new Map(
            (permissions.connections || []).map(connection => [connection.id, connection])
          );
          if (protectedProbeRun.current !== probeRun) return;
          setStatus(current => ({
            ...current,
            connections: current.connections.map(connection => ({
              ...connection,
              ...(permissionById.get(connection.id) || {
                ordersReady: false,
                abandonedCheckoutsReady: false,
                orderAccessStatus: 'Verification unavailable',
                abandonedCheckoutAccessStatus: 'Verification unavailable',
              }),
            })),
          }));
          probeSucceeded = true;
        } catch (error) {
          console.warn('Shopify protected access probe unavailable:', error);
          if (protectedProbeRun.current === probeRun) setProtectedAccessProbeFailed(true);
        } finally {
          if (protectedProbeRun.current === probeRun && probeSucceeded) {
            setProtectedAccessChecked(true);
          }
        }
      })();
      return payload;
    } catch (error) {
      console.error('Shopify status error:', error);
      setup.reportError('Failed to load Shopify status');
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id, user]);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('shopify_success');
    const error = params.get('shopify_error');
    const shop = params.get('shopify_shop');
    if (!success && !error) return;

    if (success === 'true') {
      toast.success(shop ? `Shopify connected: ${shop}` : 'Shopify connected');
    }
    if (error) {
      setup.reportError(`Shopify connection failed: ${error}`);
    }
    for (const key of ['shopify_success', 'shopify_error', 'shopify_shop', 'shopify_connection']) {
      params.delete(key);
    }
    const next = params.toString();
    window.history.replaceState(
      {},
      '',
      next ? `${window.location.pathname}?${next}` : window.location.pathname
    );
    void fetchStatus().then(payload => {
      if (success === 'true' && !error && payload?.connected) {
        clearConnectionDraft();
        setup.onConfigured();
      }
    });
  }, [fetchStatus, setup.onConfigured, clearConnectionDraft]);

  const handleConnect = async () => {
    if (!user || !currentProject?.id) return;
    const normalizedShop = normalizeShopInput(shopDomain);
    if (!normalizedShop.endsWith('.myshopify.com')) {
      setup.reportError('Enter a myshopify.com domain');
      return;
    }
    setup.clearError();
    setConnecting(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/shopify/start', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId: currentProject.id,
          shopDomain: normalizedShop,
          returnTo: integrationSetupReturnPath('shopify', setup.returnTo, isAttuneDesktopShell()),
        }),
      });
      const payload = await response.json();
      if (!response.ok || !payload.installUrl) {
        throw new Error(payload.error || 'Unable to start Shopify OAuth');
      }
      window.location.href = payload.installUrl;
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Failed to start Shopify OAuth');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!user || !currentProject?.id || !activeConnection) return;
    setDisconnecting(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/shopify/disconnect', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId: currentProject.id,
          shopDomain: activeConnection.shop_domain,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Disconnect failed');
      toast.success('Shopify disconnected');
      await fetchStatus();
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Disconnect failed');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleReconnect = async () => {
    if (!user || !currentProject?.id || !activeConnection) return;
    setup.clearError();
    setConnecting(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/shopify/reconnect', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId: currentProject.id,
          shopDomain: activeConnection.shop_domain,
          returnTo: integrationSetupReturnPath('shopify', setup.returnTo, isAttuneDesktopShell()),
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        mode?: 'oauth_redirect' | 'token_refreshed';
        installUrl?: string;
        error?: string;
      };
      if (!response.ok) throw new Error(payload.error || 'Shopify reconnect failed');
      if (payload.mode === 'oauth_redirect' && payload.installUrl) {
        window.location.href = payload.installUrl;
        return;
      }
      if (payload.mode !== 'token_refreshed') {
        throw new Error('Shopify reconnect returned an unexpected response');
      }
      toast.success('Shopify order access refreshed');
      await fetchStatus();
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Shopify reconnect failed');
    } finally {
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <IntegrationCard
        icon={<ShoppingBag size={18} />}
        title="Shopify"
        description="Product catalog and CMS page mapping for AI Research."
        statusLabel="Loading"
        statusTone="idle"
      >
        <IntegrationSetupError message={setup.error} />
        <IntegrationStatusRow icon={<InlineSpinner size="xs" />} title="Loading status" />
      </IntegrationCard>
    );
  }

  const writeReady = Boolean(activeConnection?.capabilities.writeProducts);
  const contentReady = Boolean(activeConnection?.capabilities.writeContent);
  const analyticsReady = Boolean(activeConnection?.capabilities.readReports);
  const orderReady =
    activeConnection?.ordersReady ??
    (protectedAccessChecked && Boolean(activeConnection?.capabilities.readOrders));
  const abandonedCheckoutReady = activeConnection?.abandonedCheckoutsReady ?? orderReady;
  const fullyReady =
    writeReady && contentReady && analyticsReady && orderReady && abandonedCheckoutReady;
  const connectedAt = activeConnection?.installed_at
    ? new Date(activeConnection.installed_at).toLocaleDateString()
    : null;

  return (
    <IntegrationCard
      icon={<ShoppingBag size={18} />}
      title="Shopify"
      description="Product and content operations, analytics, recent orders, abandoned checkouts, and approved AI updates."
      statusLabel={
        status.connected ? (fullyReady ? 'Full access' : 'Partial access') : 'Not connected'
      }
      statusTone={status.connected ? (fullyReady ? 'connected' : 'warning') : 'idle'}
      action={
        status.connected && activeConnection ? (
          <HStack gap="1">
            {canManage ? (
              <Button
                bg="accent.solid"
                color="accent.contrast"
                _hover={{ bg: 'accent.emphasized' }}
                borderRadius="glassButton"
                size="sm"
                onClick={handleReconnect}
                disabled={connecting}
              >
                {connecting ? <InlineSpinner size="xs" /> : <RefreshCw size={14} />}
                <Text ml={1}>
                  {connecting
                    ? 'Reconnecting...'
                    : orderReady && abandonedCheckoutReady
                      ? 'Refresh order access'
                      : 'Reconnect for order access'}
                </Text>
              </Button>
            ) : null}
            {!activeConnection.app_config_id ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDisconnect}
                disabled={disconnecting}
                color="ui.text.tertiary"
                _hover={{ color: 'status.danger.fg', bg: 'ui.surface.card' }}
              >
                {disconnecting ? <InlineSpinner size="xs" /> : <Unlink size={14} />}
                <Text ml={1}>Disconnect</Text>
              </Button>
            ) : null}
          </HStack>
        ) : hasIncompleteManagedProvisioning ? null : (
          <Button
            bg="accent.solid"
            color="accent.contrast"
            _hover={{ bg: 'accent.emphasized' }}
            borderRadius="glassButton"
            size="sm"
            onClick={handleConnect}
            disabled={connecting || !status.configured}
          >
            {connecting ? <InlineSpinner size="xs" /> : <ExternalLink size={14} />}
            <Text ml={2}>{connecting ? 'Connecting...' : 'Connect'}</Text>
          </Button>
        )
      }
    >
      <IntegrationSetupError message={setup.error} />
      {status.connected && activeConnection ? (
        <>
          <IntegrationStatusRow
            icon={writeReady ? <Check size={16} /> : <AlertCircle size={16} />}
            title={activeConnection.shop_domain}
            description={connectedAt ? `Connected ${connectedAt}` : 'Connected'}
          />
          <IntegrationStatusRow
            icon={<ShieldCheck size={16} />}
            title="Scopes"
            description={activeConnection.scopes.join(', ') || 'unknown'}
          />
          {activeConnections.length > 1 ? (
            <HStack gap="2" align="center">
              <Text fontSize="sm" color="ui.text.secondary" minW="12">
                Store
              </Text>
              <NativeSelect.Root size="sm" maxW="320px">
                <NativeSelect.Field
                  value={activeConnection.shop_domain}
                  onChange={event => setSelectedShopDomain(event.target.value)}
                  aria-label="Shopify store"
                >
                  {activeConnections.map(connection => (
                    <option key={connection.id} value={connection.shop_domain}>
                      {connection.shop_domain}
                    </option>
                  ))}
                </NativeSelect.Field>
              </NativeSelect.Root>
            </HStack>
          ) : null}
          <IntegrationStatusRow
            icon={<PackageSearch size={16} />}
            title="AI Research tools"
            description="Products, content, metafields, ShopifyQL analytics, recent orders, abandoned checkouts, and approval-gated writes"
          />
          <IntegrationStatusRow
            icon={
              orderReady && abandonedCheckoutReady ? <Check size={16} /> : <AlertCircle size={16} />
            }
            title="Orders and abandoned checkouts"
            description={
              orderReady && abandonedCheckoutReady
                ? 'Live, manager-only reads are enabled for Shopify’s latest 60-day window.'
                : protectedAccessProbeFailed
                  ? 'Shopify protected access could not be verified. Retry shortly. No customer contact fields are exposed.'
                  : activeConnection?.abandonedCheckoutAccessStatus === 'Permission required'
                    ? 'The Shopify approver must grant abandoned checkout permission, then reconnect. No customer contact fields are exposed.'
                    : activeConnection?.orderAccessStatus === 'Approval required'
                      ? 'Shopify protected order-data approval is required, then reconnect. No customer contact fields are exposed.'
                      : activeConnection?.orderAccessStatus === 'Manager verification required'
                        ? 'A project manager must verify Shopify protected-data access. No customer contact fields are exposed.'
                        : canManage
                          ? 'Reconnect to grant read_orders. No customer contact fields are exposed.'
                          : 'A project manager must reconnect to grant read_orders. No customer contact fields are exposed.'
            }
          />
          {!fullyReady && (
            <IntegrationNotice icon={<AlertCircle size={16} />} tone="warning">
              Reconnect with write_products, write_content, read_reports, and read_orders for the
              full Shopify toolset. Merchant-owned custom apps must deploy and release the updated
              app version in Shopify before reconnecting.
            </IntegrationNotice>
          )}
        </>
      ) : (
        <>
          {hasIncompleteManagedProvisioning ? (
            <>
              <IntegrationStatusRow
                icon={<ShieldCheck size={16} />}
                title={managedShopDomain || 'Admin-managed Shopify setup'}
                description="This store binding and its credentials are managed by Attune."
              />
              <IntegrationNotice icon={<AlertCircle size={16} />} tone="warning">
                Shopify setup is still in progress. An Attune administrator must finish credential
                validation and installation before this project can connect.
              </IntegrationNotice>
            </>
          ) : (
            <IntegrationStatusRow
              icon={<ShoppingBag size={16} />}
              title="Store domain"
              description="Use the permanent myshopify.com domain for OAuth."
              action={
                <HStack gap="2">
                  <Input
                    value={shopDomain}
                    onChange={event => setShopDomain(event.target.value)}
                    placeholder="store.myshopify.com"
                    size="sm"
                    maxW="240px"
                  />
                </HStack>
              }
            />
          )}
          {!status.configured && !hasIncompleteManagedProvisioning && (
            <IntegrationNotice icon={<AlertCircle size={16} />} tone="warning">
              Configure SHOPIFY_CLIENT_ID, SHOPIFY_CLIENT_SECRET, and SHOPIFY_OAUTH_STATE_SECRET.
            </IntegrationNotice>
          )}
        </>
      )}
    </IntegrationCard>
  );
}
